import { Flex, Kbd } from "@mantine/core";

interface KeyboardShortcutProps {
  /** The keyboard shortcut to display, e.g., "mod+J" or "⌘K"
   * @example
   * // Basic usage with command key - renders: <Kbd>⌘</Kbd> + <Kbd>J</Kbd>
   * <KeyboardShortcut shortcut="⌘J" />
   *
   * @example
   * // Multiple keys with modifier - renders: <Kbd>⌘</Kbd> + <Kbd>Shift</Kbd> + <Kbd>K</Kbd>
   * <KeyboardShortcut shortcut="⌘+Shift+K" />
   *
   * @example
   * // Cross-platform mod key - renders: <Kbd>⌘</Kbd> + <Kbd>J</Kbd> (on Mac)
   * <KeyboardShortcut shortcut="mod+J" />
   */
  shortcut: string;
  /** Optional separator between keys, defaults to "+" */
  separator?: string;
}

/**
 * KeyboardShortcut component displays keyboard shortcuts using Mantine's Kbd component
 * for consistent styling across the application.
 *
 * Features:
 * - Automatically converts "mod" to platform-specific command key (⌘)
 * - Splits keyboard combinations into individual key elements
 * - Consistent styling with Mantine's design system
 * - Accessible and semantic HTML structure
 */
export const KeyboardShortcut = ({ shortcut, separator = "+" }: KeyboardShortcutProps) => {
  // Convert mod+key notation to platform-specific keys
  const normalizedShortcut = shortcut.replace(/mod/g, "⌘");

  const keys: string[] = (() => {
    // If the shortcut contains explicit separators, split by them
    if (normalizedShortcut.includes(separator)) {
      return normalizedShortcut.split(separator);
    }

    // Handle cases like "⌘J" where there's no explicit separator
    // Split between command symbol and the following character(s)
    const match = normalizedShortcut.match(/^(⌘)(.+)$/);
    if (match) {
      return [match[1], match[2]];
    }

    // Fallback for other formats
    return [normalizedShortcut];
  })();

  return (
    <Flex align="center" gap="0.25rem" display="inline-flex">
      {keys
        .flatMap((key, index) => [
          <Kbd key={key.trim()} size="xs">
            {key.trim()}
          </Kbd>,
          index < keys.length - 1 && <span key={`sep-${key.trim()}`}>{separator}</span>,
        ])
        .filter(Boolean)}
    </Flex>
  );
};
